import React, { useRef, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Input, Alert } from 'reactstrap'
import gql from 'graphql-tag'
import Select from 'react-select'
import "./UploadIncidentDocumentModal.css"

const AllIncidentDocumentTypes = gql`
  query AllIncidentDocumentTypes($category: String!) {
    allIncidentDocumentTypes(category: $category, isActive: true) {
      edges {
        node {
          id
          name
          description
          category
        }
      }
    }
  }
`

const CreateIncidentDocument = gql`
  mutation CreateIncidentDocument($input: CreateIncidentDocumentMutationInput!) {
    createIncidentDocument(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const GenerateGcsUploadUrl = gql`
  mutation GenerateGcsUploadUrl($input: GenerateGCSUploadURLInput!) {
    generateGcsUploadUrl(input: $input) {
      ok
      uploadUrl
      blobPath
      errors {
        field
        messages
      }
    }
  }
`

const UploadIncidentDocumentModal = ({ incidentId, isOpen, toggle, refetchQuery, documentCategory, slug }) => {
    const { data: incidentDocumentTypes, loading: typesLoading } = useQuery(AllIncidentDocumentTypes, {
        variables: { category: documentCategory },
    })

    const [createIncidentDocument] = useMutation(CreateIncidentDocument)
    const [generateGcsUploadUrl] = useMutation(GenerateGcsUploadUrl)

    const [selectedType, setSelectedType] = useState(null)
    const [selectedFiles, setSelectedFiles] = useState([])
    const [notes, setNotes] = useState('')
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [isDragActive, setIsDragActive] = useState(false)

    const fileInputRef = useRef(null)

    const clearAllFiles = () => {
        setSelectedFiles((currentFiles) => {
            currentFiles.forEach((file) => {
                if (file.isVideo && file.preview?.startsWith('blob:')) {
                    URL.revokeObjectURL(file.preview)
                }
            })
            return []
        })
    }

    const processFiles = (files) => {
        if (!files?.length) return

        let completed = 0
        const preparedFiles = new Array(files.length)

        const finalize = () => {
            completed += 1
            if (completed !== files.length) return

            setSelectedFiles((prev) => [...prev, ...preparedFiles.filter(Boolean)])
        }

        files.forEach((file, index) => {
            const isVideo = file?.type?.startsWith('video/')
            const baseFile = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                name: file.name,
                type: file.type,
                file,
                isVideo,
                preview: isVideo ? URL.createObjectURL(file) : '',
                documentBase64: null,
                uploadStatus: isVideo ? 'pending' : null,
                uploadProgress: 0,
            }

            if (isVideo) {
                preparedFiles[index] = baseFile
                finalize()
                return
            }

            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
                const dataUrl = reader.result || ''
                const commaIndex = dataUrl.indexOf(',')
                preparedFiles[index] = {
                    ...baseFile,
                    preview: dataUrl,
                    documentBase64: commaIndex >= 0 ? dataUrl.substring(commaIndex + 1) : null,
                }
                finalize()
            }
            reader.onerror = () => {
                preparedFiles[index] = baseFile
                finalize()
            }
        })
    }

    const updateVideoProgress = (fileId, status, progress) => {
        setSelectedFiles((prev) => prev.map((file) => (
            file.id === fileId
                ? { ...file, uploadStatus: status, uploadProgress: progress }
                : file
        )))
    }

    // Builds a stream body for video upload and updates progress as chunks are uploaded.
    const buildVideoUploadBody = (file, fileId) => {
        if (!file?.stream || typeof ReadableStream === 'undefined' || !file.size) {
            return file
        }

        const reader = file.stream().getReader()
        let uploadedBytes = 0

        return new ReadableStream({
            async pull(controller) {
                const { done, value } = await reader.read()
                if (done) {
                    controller.close()
                    return
                }

                uploadedBytes += value.byteLength
                const progress = Math.min(100, Math.round((uploadedBytes / file.size) * 100))
                updateVideoProgress(fileId, 'uploading', progress)
                controller.enqueue(value)
            },
            cancel(reason) {
                reader.cancel(reason)
            },
        })
    }

    const uploadVideo = async (videoFile) => {
        const contentType = videoFile.type || 'video/mp4'
        updateVideoProgress(videoFile.id, 'uploading', 0)

        let gcsResponse = null
        try {
            gcsResponse = await generateGcsUploadUrl({
                variables: {
                    input: {
                        folder: 'incidents',
                        contentType,
                    },
                },
            })
        } catch (mutationError) {
            throw new Error(mutationError?.message || 'Unable to generate upload URL.')
        }

        const gcsData = gcsResponse?.data?.generateGcsUploadUrl
        if (!gcsData?.ok || !gcsData?.uploadUrl || !gcsData?.blobPath) {
            const message = gcsData?.errors?.map((e) => e.messages.join(', ')).join('; ')
            throw new Error(message || 'Unable to generate upload URL.')
        }

        const body = buildVideoUploadBody(videoFile.file, videoFile.id)
        const requestOptions = {
            method: 'PUT',
            headers: { 'Content-Type': contentType },
            body,
        }

        if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) {
            requestOptions.duplex = 'half'
        }

        const uploadResponse = await fetch(gcsData.uploadUrl, requestOptions)
        if (!uploadResponse.ok) {
            let serverMessage = ''
            try {
                serverMessage = await uploadResponse.text()
            } catch (e) {
                serverMessage = ''
            }
            const responseMessage = serverMessage ? ` - ${serverMessage.slice(0, 200)}` : ''
            throw new Error(`Video upload failed (${uploadResponse.status}).${responseMessage}`)
        }

        let saveVideoResponse
        try {
            saveVideoResponse = await createIncidentDocument({
                variables: {
                    input: {
                        incidentId,
                        documentType: selectedType.value,
                        blobPath: gcsData.blobPath,
                        notes: notes || '',
                        slug,
                    },
                },
            })
        } catch (mutationError) {
            throw new Error(mutationError?.message || 'Error uploading document.')
        }

        if (!saveVideoResponse?.data?.createIncidentDocument?.ok) {
            const message = saveVideoResponse?.data?.createIncidentDocument?.errors?.map((e) => e.messages.join(', ')).join('; ')
            throw new Error(message || 'Error uploading document.')
        }

        updateVideoProgress(videoFile.id, 'uploaded', 100)
    }

    const uploadVideosWithConcurrency = async (videoFiles, failedFiles, successfulFileIds) => {
        let index = 0

        // Each worker picks the next video and uploads it until all videos are processed.
        const processVideoQueue = async () => {
            while (index < videoFiles.length) {
                const currentIndex = index
                index += 1
                const file = videoFiles[currentIndex]

                try {
                    await uploadVideo(file)
                    successfulFileIds.push(file.id)
                } catch (uploadError) {
                    updateVideoProgress(file.id, 'error', file.uploadProgress || 0)
                    failedFiles.push({
                        id: file.id,
                        name: file.name,
                        message: uploadError?.message || 'Video upload failed.',
                    })
                }
            }
        }

        const workerCount = Math.min(2, videoFiles.length)
        if (workerCount <= 1) {
            await processVideoQueue()
            return
        }
        // Start multiple workers to process the video upload queue concurrently.
        await Promise.all([processVideoQueue(), processVideoQueue()])
    }

    const handleUpload = async () => {
        setError(null)
        setSuccess(false)

        if (!selectedType) {
            setError('Please select a document type.')
            return
        }

        if (!selectedFiles.length) {
            setError('Please select at least one file to upload.')
            return
        }

        setIsUploading(true)

        const successfulFileIds = []
        const failedFiles = []

        try {
            const otherFiles = selectedFiles.filter((file) => !file.isVideo)
            const videoFiles = selectedFiles.filter((file) => file.isVideo)

            if (otherFiles.length) {
                const readableOtherFiles = otherFiles.filter((file) => file.documentBase64)
                const otherDocuments = readableOtherFiles.map((file) => file.documentBase64)

                otherFiles.forEach((file) => {
                    if (!file.documentBase64) {
                        failedFiles.push({ id: file.id, name: file.name, message: 'Could not read file.' })
                    }
                })

                if (otherDocuments.length) {
                    let saveOtherFilesResponse
                    try {
                        saveOtherFilesResponse = await createIncidentDocument({
                            variables: {
                                input: {
                                    incidentId,
                                    documentType: selectedType.value,
                                    documents: otherDocuments,
                                    notes: notes || '',
                                    slug,
                                },
                            },
                        })
                    } catch (mutationError) {
                        const message = mutationError?.message || 'Error uploading document.'
                        readableOtherFiles.forEach((file) => {
                            failedFiles.push({ id: file.id, name: file.name, message })
                        })
                        saveOtherFilesResponse = null
                    }

                    if (saveOtherFilesResponse && !saveOtherFilesResponse?.data?.createIncidentDocument?.ok) {
                        const message = saveOtherFilesResponse?.data?.createIncidentDocument?.errors?.map((e) => e.messages.join(', ')).join('; ')
                        readableOtherFiles.forEach((file) => {
                            failedFiles.push({ id: file.id, name: file.name, message })
                        })
                    } else if (saveOtherFilesResponse) {
                        readableOtherFiles.forEach((file) => {
                            successfulFileIds.push(file.id)
                        })
                    }
                }
            }

            if (videoFiles.length) {
                setSelectedFiles((prev) => prev.map((file) => (
                    file.isVideo ? { ...file, uploadStatus: 'pending', uploadProgress: 0 } : file
                )))
                await uploadVideosWithConcurrency(videoFiles, failedFiles, successfulFileIds)
            }

            if (!failedFiles.length) {
                setSuccess(true)
                clearAllFiles()
                setNotes('')
                setSelectedType(null)
                toggle()
                if (refetchQuery) refetchQuery()
                return
            }

            const failedIds = new Set(failedFiles.map((file) => file.id))

            const firstFailureMessage = failedFiles[0]?.message

            if (successfulFileIds.length > 0) {
                setError(`${successfulFileIds.length} file(s) uploaded, ${failedFiles.length} failed. ${firstFailureMessage || 'Please retry failed files.'}`)
            } else {
                setError(firstFailureMessage || `Upload failed for ${failedFiles.length} file(s). Please retry.`)
            }

            setSelectedFiles((prev) => {
                prev.forEach((file) => {
                    if (!failedIds.has(file.id) && file.isVideo && file.preview?.startsWith('blob:')) {
                        URL.revokeObjectURL(file.preview)
                    }
                })

                return prev
                    .filter((file) => failedIds.has(file.id))
                    .map((file) => (file.isVideo ? { ...file, uploadStatus: 'error' } : file))
            })
        } catch (uploadError) {
            setError(uploadError.message || 'Error uploading document.')
            setSelectedFiles((prev) => prev.map((file) => (
                file.isVideo && file.uploadStatus === 'uploading'
                    ? { ...file, uploadStatus: 'error' }
                    : file
            )))
        } finally {
            setIsUploading(false)
        }
    }

    const handleModalClosed = () => {
        setError(null)
        setSuccess(false)
        clearAllFiles()
        setNotes('')
        setSelectedType(null)
    }

    const handleFileChange = (e) => {
        if (isUploading) return
        processFiles(Array.from(e.target.files))
        e.target.value = null
    }

    const handleDrop = (e) => {
        if (isUploading) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)
        processFiles(Array.from(e.dataTransfer.files))
    }

    const handleDragOver = (e) => {
        if (isUploading) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(true)
    }

    const handleDragLeave = (e) => {
        if (isUploading) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)
    }

    const handleAreaClick = () => {
        if (isUploading) return
        fileInputRef.current?.click()
    }

    const removeFile = (index) => {
        if (isUploading) return

        setSelectedFiles((prev) => {
            const removed = prev[index]
            if (removed?.isVideo && removed.preview?.startsWith('blob:')) {
                URL.revokeObjectURL(removed.preview)
            }
            return prev.filter((_, i) => i !== index)
        })
    }

    return (
        <Modal isOpen={isOpen} onClosed={handleModalClosed} className="incident-document-modal" keyboard={!isUploading} backdrop={isUploading ? 'static' : true}>
            <ModalHeader>Upload Document</ModalHeader>
            <ModalBody>
                {error && <Row className="mb-2"><Col><Alert color="danger">{error}</Alert></Col></Row>}
                {success && <Row className="mb-2"><Col><Alert color="success">Document uploaded successfully!</Alert></Col></Row>}
                <Row className="mb-3">
                    <Col>
                        <FormGroup>
                            <Label>Document Type</Label>
                            <Select
                                options={incidentDocumentTypes?.allIncidentDocumentTypes?.edges?.map((opt) => ({ value: opt.node.id, label: opt.node.name })) || []}
                                value={selectedType}
                                onChange={setSelectedType}
                                isDisabled={typesLoading || isUploading}
                                placeholder="Select document type..."
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <FormGroup>
                            <Label>File</Label><br />
                            <div
                                className={`drag-drop-area${isDragActive ? ' drag-drop-active' : ''}`}
                                onClick={handleAreaClick}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                            >
                                {isDragActive ? <span className="drag-drop-anim">Release to upload files</span> : <span>Drag & drop files here or click to select</span>}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                    accept=".pdf,image/*,video/*"
                                    multiple
                                    disabled={isUploading}
                                />
                            </div>
                            <div className="selected-files-preview">
                                {selectedFiles.map((file, index) => (
                                    <div key={file.id} className="file-preview-item">
                                        <div className="file-preview-container">
                                            <i className="fa fa-times" onClick={() => removeFile(index)}></i>
                                            {file.type.startsWith('image/') && file.preview ? (
                                                <img src={file.preview} alt={file.name} className="file-preview-img" />
                                            ) : file.type === 'application/pdf' ? (
                                                <span className="file-preview-pdf">PDF</span>
                                            ) : file.type.startsWith('video/') ? (
                                                <span className="file-preview-video">🎬</span>
                                            ) : (
                                                <span className="file-preview-name">{file.name}</span>
                                            )}
                                        </div>
                                        <div className="file-preview-name">{file.name}</div>
                                        {file.isVideo && file.uploadStatus !== 'pending' && (
                                            <div className="file-upload-progress-wrapper">
                                                <div
                                                    className={`file-upload-progress-bar status-${file.uploadStatus || 'pending'}`}
                                                    style={{ width: `${file.uploadProgress || 0}%` }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <FormGroup>
                            <Label>Notes</Label>
                            <Input
                                type="textarea"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                disabled={isUploading}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button className="incident-document-modal-secondary-btn" onClick={toggle} disabled={isUploading}>Cancel</Button>
                <Button className="incident-document-modal-primary-btn" onClick={handleUpload} disabled={isUploading}>
                    {isUploading ? 'Uploading...' : 'Upload'}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default UploadIncidentDocumentModal
